import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Car, Plus, Trash, PencilSimple, Receipt, FileText, DownloadSimple, Tag, ArrowRight, Camera, UserCircle } from '@phosphor-icons/react';
import { VehicleFormModal, VehicleExpenseModal, VehicleDocModal, VehicleSellModal } from '../components/vehicles/VehicleModals';
import { VehicleDetail } from '../components/vehicles/VehicleDetail';
import { useSearch } from '../components/VerticalTabs';
import { useAuth } from '../context/AuthContext';
import { LayoutToggle, useLayoutMode, getLayoutContainerProps } from '../components/ui/LayoutToggle';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VEHICLE_TYPES = [
  { value: 'utilitaire', label: 'Utilitaire' },
  { value: 'tourisme', label: 'Tourisme' }
];
const CONDITIONS = [
  { value: 'neuf', label: 'Neuf' },
  { value: 'bon', label: 'Bon état' },
  { value: 'usage', label: 'Usagé' },
  { value: 'reparation', label: 'En réparation' },
  { value: 'hors_service', label: 'Hors service' }
];

const VehiclesPage = ({ embedded }) => {
  const { user: currentUser } = useAuth();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editVehicle, setEditVehicle] = useState(null);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [layoutMode, setLayoutMode] = useLayoutMode('vehicles', 'grid');
  const [vehicleExpenses, setVehicleExpenses] = useState([]);
  const [vehicleDocs, setVehicleDocs] = useState([]);
  const [activeTab, setActiveTab] = useState('info');
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [showDocModal, setShowDocModal] = useState(false);
  const [showSellModal, setShowSellModal] = useState(false);
  const [sellForm, setSellForm] = useState({ sold_date: '', sold_price: 0 });
  const [form, setForm] = useState({
    brand: '', model: '', vehicle_type: 'utilitaire', purchase_date: '',
    registration: '', insurance_start: '', insurance_end: '',
    technical_control_date: '', condition: 'bon', purchase_price: 0, notes: '',
    assigned_user_id: ''
  });
  const [employees, setEmployees] = useState([]);
  const [expenseForm, setExpenseForm] = useState({ category: 'entretien', description: '', amount_ht: 0, tva_rate: 20, date: '' });
  const [docForm, setDocForm] = useState({ title: '', category: 'carte_grise', file: null });

  const fetchVehicles = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/vehicles`, { withCredentials: true });
      setVehicles(res.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [API_URL]);

  useEffect(() => { fetchVehicles(); }, [fetchVehicles]);

  // Fetch employees once for the assignment dropdown
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/users/employees`, { withCredentials: true });
        setEmployees((res.data || []).filter(u => !u.archived));
      } catch (e) { /* noop */ }
    })();
  }, []);

  const fetchVehicleDetails = async (vehicleId) => {
    try {
      const [expRes, docRes] = await Promise.all([
        axios.get(`${API_URL}/api/vehicles/${vehicleId}/expenses`, { withCredentials: true }),
        axios.get(`${API_URL}/api/vehicles/${vehicleId}/documents`, { withCredentials: true })
      ]);
      setVehicleExpenses(expRes.data);
      setVehicleDocs(docRes.data);
    } catch (error) {
      console.error('Erreur chargement details vehicule:', error);
    }
  };

  const selectVehicle = (v) => {
    setSelectedVehicle(v);
    setActiveTab('info');
    fetchVehicleDetails(v.id);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, purchase_price: parseFloat(form.purchase_price) || 0 };
      if (editVehicle) {
        await axios.put(`${API_URL}/api/vehicles/${editVehicle.id}`, payload, { withCredentials: true });
        toast.success('Véhicule mis à jour');
      } else {
        await axios.post(`${API_URL}/api/vehicles`, payload, { withCredentials: true });
        toast.success('Véhicule ajouté');
      }
      setShowModal(false);
      resetForm();
      fetchVehicles();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce véhicule et toutes ses données ?')) return;
    try {
      await axios.delete(`${API_URL}/api/vehicles/${id}`, { withCredentials: true });
      toast.success('Véhicule supprimé');
      setSelectedVehicle(null);
      fetchVehicles();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleSell = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API_URL}/api/vehicles/${selectedVehicle.id}`, {
        sold: true, sold_date: sellForm.sold_date, sold_price: parseFloat(sellForm.sold_price) || 0
      }, { withCredentials: true });
      toast.success('Véhicule marqué comme vendu');
      setShowSellModal(false);
      fetchVehicles();
      setSelectedVehicle(null);
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleAddExpense = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/vehicles/${selectedVehicle.id}/expenses`, {
        ...expenseForm, amount_ht: parseFloat(expenseForm.amount_ht) || 0
      }, { withCredentials: true });
      toast.success('Dépense ajoutée');
      setShowExpenseModal(false);
      setExpenseForm({ category: 'entretien', description: '', amount_ht: 0, tva_rate: 20, date: '' });
      fetchVehicleDetails(selectedVehicle.id);
      fetchVehicles();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleDeleteExpense = async (expId) => {
    try {
      await axios.delete(`${API_URL}/api/vehicles/${selectedVehicle.id}/expenses/${expId}`, { withCredentials: true });
      toast.success('Dépense supprimée');
      fetchVehicleDetails(selectedVehicle.id);
      fetchVehicles();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleUploadDoc = async (e) => {
    e.preventDefault();
    if (!docForm.file) return toast.error('Veuillez sélectionner un fichier');
    try {
      const fd = new FormData();
      fd.append('file', docForm.file);
      fd.append('title', docForm.title || docForm.file.name);
      fd.append('category', docForm.category);
      await axios.post(`${API_URL}/api/vehicles/${selectedVehicle.id}/documents`, fd, { withCredentials: true });
      toast.success('Document ajouté');
      setShowDocModal(false);
      setDocForm({ title: '', category: 'carte_grise', file: null });
      fetchVehicleDetails(selectedVehicle.id);
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleDeleteDoc = async (docId) => {
    try {
      await axios.delete(`${API_URL}/api/vehicles/${selectedVehicle.id}/documents/${docId}`, { withCredentials: true });
      toast.success('Document supprimé');
      fetchVehicleDetails(selectedVehicle.id);
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handlePhotoUpload = async (vehicleId, file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('Photo trop lourde (max 10 Mo)'); return; }
    const fd = new FormData();
    fd.append('file', file);
    try {
      await axios.post(`${API_URL}/api/vehicles/${vehicleId}/photo`, fd, { withCredentials: true });
      toast.success('Photo ajoutee');
      fetchVehicles();
      if (selectedVehicle?.id === vehicleId) {
        const res = await axios.get(`${API_URL}/api/vehicles`, { withCredentials: true });
        const fresh = (res.data || []).find(v => v.id === vehicleId);
        if (fresh) setSelectedVehicle(fresh);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur upload photo');
    }
  };

  const handlePhotoDelete = async (vehicleId) => {
    if (!window.confirm('Supprimer la photo ?')) return;
    try {
      await axios.delete(`${API_URL}/api/vehicles/${vehicleId}/photo`, { withCredentials: true });
      toast.success('Photo supprimee');
      fetchVehicles();
      if (selectedVehicle?.id === vehicleId) {
        setSelectedVehicle({ ...selectedVehicle, photo_url: '' });
      }
    } catch (e) {
      toast.error('Erreur');
    }
  };

  const openEdit = (v) => {
    setEditVehicle(v);
    setForm({
      brand: v.brand, model: v.model, vehicle_type: v.vehicle_type,
      purchase_date: v.purchase_date || '', registration: v.registration || '',
      insurance_start: v.insurance_start || '', insurance_end: v.insurance_end || '',
      technical_control_date: v.technical_control_date || '', condition: v.condition || 'bon',
      purchase_price: v.purchase_price || 0, notes: v.notes || '',
      assigned_user_id: v.assigned_user_id || ''
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditVehicle(null);
    setForm({ brand: '', model: '', vehicle_type: 'utilitaire', purchase_date: '', registration: '', insurance_start: '', insurance_end: '', technical_control_date: '', condition: 'bon', purchase_price: 0, notes: '', assigned_user_id: '' });
  };

  const conditionBadge = (cond) => {
    const colors = { neuf: '#2E7D32', bon: '#1565C0', usage: '#EF6C00', reparation: '#C62828', hors_service: '#424242' };
    const label = CONDITIONS.find(c => c.value === cond)?.label || cond;
    return <span style={{ padding: '2px 8px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: '600', backgroundColor: `${colors[cond] || '#666'}15`, color: colors[cond] || '#666' }}>{label}</span>;
  };

  const { searchTerm } = useSearch();

  if (loading) return <div className="loading-container"><div className="spinner" /></div>;

  if (selectedVehicle) {
    return (
      <VehicleDetail
        vehicle={selectedVehicle}
        activeTab={activeTab} setActiveTab={setActiveTab}
        vehicleExpenses={vehicleExpenses} vehicleDocs={vehicleDocs}
        employees={employees}
        currentUser={currentUser}
        onAssignChange={fetchVehicles}
        onBack={() => setSelectedVehicle(null)}
        onEdit={openEdit}
        onDelete={handleDelete}
        onSell={() => { setSellForm({ sold_date: '', sold_price: 0 }); setShowSellModal(true); }}
        onShowExpenseModal={() => setShowExpenseModal(true)}
        onShowDocModal={() => setShowDocModal(true)}
        onDeleteExpense={handleDeleteExpense}
        onDeleteDoc={handleDeleteDoc}
        ExpenseModal={showExpenseModal && <VehicleExpenseModal expenseForm={expenseForm} setExpenseForm={setExpenseForm} onClose={() => setShowExpenseModal(false)} onSubmit={handleAddExpense} />}
        DocModal={showDocModal && <VehicleDocModal docForm={docForm} setDocForm={setDocForm} onClose={() => setShowDocModal(false)} onSubmit={handleUploadDoc} />}
        SellModal={showSellModal && <VehicleSellModal sellForm={sellForm} setSellForm={setSellForm} onClose={() => setShowSellModal(false)} onSubmit={handleSell} />}
      />
    );
  }

  const filteredVehicles = vehicles.filter(v => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return (v.brand || '').toLowerCase().includes(s) || (v.model || '').toLowerCase().includes(s) || (v.registration || '').toLowerCase().includes(s);
  });

  // List view
  return (
    <div data-testid="vehicles-page">
      {!embedded && (
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h1 className="page-title">Vehicules</h1>
          <p className="page-subtitle">{filteredVehicles.length} vehicule(s)</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <LayoutToggle layout={layoutMode} setLayout={setLayoutMode} testidPrefix="vehicles-layout" />
          <button data-testid="add-vehicle-btn" className="btn btn-primary" onClick={() => { resetForm(); setShowModal(true); }}>
            <Plus size={18} weight="bold" /> Ajouter
          </button>
        </div>
      </div>
      )}
      {embedded && (
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
          <p style={{ color: '#5E5F66', fontSize: '0.9rem', margin: 0, alignSelf: 'center' }}>{filteredVehicles.length} vehicule(s)</p>
          <LayoutToggle layout={layoutMode} setLayout={setLayoutMode} testidPrefix="vehicles-layout" />
          <button data-testid="add-vehicle-btn" className="btn btn-primary" onClick={() => { resetForm(); setShowModal(true); }}>
            <Plus size={18} weight="bold" /> Ajouter
          </button>
        </div>
      )}

      {filteredVehicles.length > 0 ? (
        (() => {
          const lp = getLayoutContainerProps(layoutMode, { cardMinWidth: '360px' });
          return (
            <div style={lp.style} className={lp.className || ''} data-testid={`vehicles-container-${layoutMode}`}>
          {filteredVehicles.map((v) => (
            <div key={v.id} className="card" style={{ cursor: 'pointer', transition: 'box-shadow 0.2s', overflow: 'hidden' }} onClick={() => selectVehicle(v)}>
              {v.photo_url ? (
                <div style={{ position: 'relative', width: '100%', height: '160px', background: '#F4F5F7' }}>
                  <img
                    src={`${API_URL}${v.photo_url}?v=${v.id}`}
                    alt={`${v.brand} ${v.model}`}
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  />
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); handlePhotoDelete(v.id); }}
                    data-testid={`veh-photo-delete-${v.id}`}
                    style={{ position: 'absolute', top: '8px', right: '8px', background: 'rgba(0,0,0,0.6)', border: 'none', color: 'white', width: '28px', height: '28px', borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    title="Supprimer la photo"
                  ><Trash size={14} /></button>
                </div>
              ) : (
                <label
                  onClick={(e) => e.stopPropagation()}
                  data-testid={`veh-photo-add-${v.id}`}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '80px', background: 'linear-gradient(135deg, #EEF1FC, #E2E8F7)', color: '#5E5F66', cursor: 'pointer', fontSize: '0.82rem', gap: '6px', borderBottom: '1px solid #E2E2E6' }}
                >
                  <Camera size={18} /> Ajouter une photo
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) handlePhotoUpload(v.id, f); e.target.value = ''; }}
                    style={{ display: 'none' }}
                  />
                </label>
              )}
              <div style={{ padding: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <Car size={28} weight="fill" style={{ color: v.sold ? '#9E9E9E' : '#002FA7' }} />
                    <div>
                      <div style={{ fontWeight: '700', fontSize: '1.1rem' }}>{v.brand} {v.model}</div>
                      <div style={{ color: '#5E5F66', fontSize: '0.85rem' }}>{v.registration}</div>
                    </div>
                  </div>
                  {v.sold ? (
                    <span style={{ padding: '4px 8px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: '600', backgroundColor: '#FFF3E0', color: '#EF6C00' }}>Vendu</span>
                  ) : (
                    conditionBadge(v.condition)
                  )}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '0.85rem' }}>
                  <div><span style={{ color: '#5E5F66' }}>Type:</span> {VEHICLE_TYPES.find(t => t.value === v.vehicle_type)?.label}</div>
                  <div><span style={{ color: '#5E5F66' }}>Achat:</span> {v.purchase_price?.toLocaleString('fr-FR')}€</div>
                  <div><span style={{ color: '#5E5F66' }}>Dépenses:</span> <span style={{ color: '#C62828', fontWeight: '600' }}>{v.total_expenses.toLocaleString('fr-FR')}€</span></div>
                  <div><span style={{ color: '#5E5F66' }}>Docs:</span> {v.docs_count}</div>
                </div>
                {v.assigned_user_name && (
                  <div data-testid={`veh-card-assigned-${v.id}`} style={{ marginTop: '10px', padding: '6px 10px', backgroundColor: '#E3F2FD', borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.78rem', color: '#1565C0', fontWeight: 600 }}>
                    <UserCircle size={14} weight="fill" /> Affecte a <strong>{v.assigned_user_name}</strong>
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '12px' }}>
                  <span style={{ color: '#002FA7', fontSize: '0.85rem', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    Voir détails <ArrowRight size={14} />
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
          );
        })()
      ) : (
        <div className="card">
          <div className="empty-state">
            <Car size={48} className="empty-state-icon" />
            <p className="empty-state-text">Aucun véhicule enregistré</p>
          </div>
        </div>
      )}

      {showModal && <VehicleFormModal editVehicle={editVehicle} form={form} setForm={setForm} onClose={() => setShowModal(false)} onSubmit={handleSubmit} employees={employees} />}
    </div>
  );
};

export default VehiclesPage;
